import { Fragment } from 'react';
import { Check } from '@phosphor-icons/react';

import { cn } from '../../utils/cn';

/**
 * `.pen` `ds/wizard/step-indicator--numbered` (Nuxwt) e `--dots` (M7wFLW).
 * Estados: `ds/wizard/step--done` (sBuZS) · `--active` (t3315) · `--upcoming` (AW0lb).
 *
 * @param {'numbered'|'dots'} [variant='numbered']
 * @param {number|Array<string|{label:string}>} steps total de passos, ou lista de rótulos
 * @param {number} [current=0] índice do passo ativo (0-based)
 */
export function WizardStepIndicator({ variant = 'numbered', steps, current = 0, className }) {
  const items = Array.isArray(steps)
    ? steps.map((s) => (typeof s === 'string' ? { label: s } : s))
    : Array.from({ length: steps }, () => ({}));
  const total = items.length;

  const stateOf = (index) => {
    if (index < current) return 'done';
    if (index === current) return 'active';
    return 'upcoming';
  };

  if (variant === 'dots') {
    return (
      <div
        data-ds-id="ds/wizard/step-indicator--dots"
        role="progressbar"
        aria-valuenow={current + 1}
        aria-valuemin={1}
        aria-valuemax={total}
        className={cn('flex items-center gap-space-2', className)}
      >
        {items.map((_, index) => {
          const state = stateOf(index);
          return (
            <span
              key={index}
              data-state={state}
              className={cn(
                'block shrink-0 transition-colors duration-300',
                state === 'active'
                  ? 'h-1.5 w-5 rounded-ds-full bg-sage-500'
                  : 'size-1.5 rounded-ds-full',
                state === 'done' && 'bg-sage-700 dark:bg-sage-800',
                state === 'upcoming' && 'bg-track',
              )}
              aria-hidden
            />
          );
        })}
      </div>
    );
  }

  return (
    <div
      data-ds-id="ds/wizard/step-indicator--numbered"
      role="progressbar"
      aria-valuenow={current + 1}
      aria-valuemin={1}
      aria-valuemax={total}
      className={cn('flex w-full items-start', className)}
    >
      {items.map((item, index) => {
        const state = stateOf(index);

        return (
          <Fragment key={index}>
            <div
              data-ds-id={`ds/wizard/step--${state}`}
              data-state={state}
              className="flex min-w-0 flex-1 flex-col items-center gap-space-2"
            >
              <span
                className={cn(
                  'grid size-7 shrink-0 place-items-center rounded-ds-full transition-colors duration-300',
                  state === 'done' && 'bg-sage-700 dark:bg-sage-800',
                  state === 'active' && 'bg-sage-tint ring-2 ring-sage-500',
                  state === 'upcoming' && 'border border-input-border bg-track',
                )}
                aria-hidden
              >
                {state === 'done' ? (
                  <Check className="size-3.5 text-sage-50" weight="bold" />
                ) : (
                  <span
                    className={cn(
                      'text-type-label font-bold tabular-nums',
                      state === 'active' ? 'text-sage-on-tint' : 'text-muted-foreground',
                    )}
                  >
                    {index + 1}
                  </span>
                )}
              </span>
              {item.label ? (
                <span
                  className={cn(
                    'w-full text-center text-type-section font-semibold leading-snug',
                    state === 'active' ? 'text-sage-on-tint' : 'text-muted-foreground',
                  )}
                >
                  {item.label}
                </span>
              ) : null}
            </div>

            {index < total - 1 ? (
              <span
                className={cn(
                  // eslint-disable-next-line no-restricted-syntax -- offset do conector (centro do círculo 28px)
                  'mt-[13px] h-0.5 min-w-0 flex-1 rounded-ds-full transition-colors duration-300',
                  state === 'done' ? 'bg-sage-700 dark:bg-sage-800' : 'bg-track',
                )}
                aria-hidden
              />
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
}

export default WizardStepIndicator;
